/**
 * Device AVL Capabilities Page
 * Visar vilka AVL-parametrar (id + namn + beskrivning) varje enhet/fordon levererar.
 * Klicka på IMEI för att öppna modal med alla parametrar inkl. beskrivning.
 */

import React, { useState, useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import {
  ArrowLeftIcon,
  DevicePhoneMobileIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  CpuChipIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { DataTable, EmptyState, LoadingSpinner, LoadingState } from '@/components/common';
import toast from 'react-hot-toast';

interface AvlParameter {
  id: number;
  name: string;
  description?: string;
}

interface DeviceAvlCapability {
  imei: string;
  vin: string;
  registration_number?: string | null;
  avl_parameters: AvlParameter[];
  first_seen: string;
  last_seen: string;
}

const DeviceAvlCapabilities: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<DeviceAvlCapability[]>([]);
  const [loading, setLoading] = useState(true);
  const [imeiFilter, setImeiFilter] = useState('');
  const [avlIdFilter, setAvlIdFilter] = useState('');
  const [regNumberFilter, setRegNumberFilter] = useState('');
  const [appliedImei, setAppliedImei] = useState('');
  const [appliedAvlId, setAppliedAvlId] = useState('');
  const [appliedRegNumber, setAppliedRegNumber] = useState('');
  const [modalRow, setModalRow] = useState<DeviceAvlCapability | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedImei) params.set('imei', appliedImei);
      if (appliedAvlId) params.set('avl_id', appliedAvlId);
      if (appliedRegNumber) params.set('reg_number', appliedRegNumber);
      const response = await api.get(
        `/api/admin/device-avl-capabilities${params.toString() ? '?' + params.toString() : ''}`
      );
      setData(response.data?.data || []);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
      const msg =
        typeof e?.response?.data?.error === 'string'
          ? e.response.data.error
          : typeof e?.response?.data?.message === 'string'
            ? e.response.data.message
            : typeof e?.message === 'string'
              ? e.message
              : 'Kunde inte hämta AVL-förmågor';
      console.error('Failed to fetch AVL capabilities:', msg, err);
      toast.error(msg);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [appliedImei, appliedAvlId, appliedRegNumber]);

  const handleApplyFilters = () => {
    setAppliedImei(imeiFilter.trim());
    setAppliedAvlId(avlIdFilter.trim());
    setAppliedRegNumber(regNumberFilter.trim());
  };

  const formatDate = (d: string) => {
    if (!d) return '–';
    const date = new Date(d);
    return date.toLocaleString('sv-SE', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  return (
    <div className="flex flex-col h-full bg-bgDefault">
      <div className="p-6 border-b border-borderDefault">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
              <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
            </button>
            <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
              <CpuChipIcon className="w-6 h-6 text-primary" />
              {t('deviceAvlCapabilities.title')}
            </h1>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-bgElevated text-textDefault border border-borderDefault rounded-lg hover:bg-bgHover transition-colors disabled:opacity-50"
          >
            {loading ? <LoadingSpinner size="sm" /> : <ArrowPathIcon className="w-5 h-5" />}
            {t('common.refresh')}
          </button>
        </div>

        <p className="text-sm text-textSecondary mb-4">
          {t('deviceAvlCapabilities.description')}
        </p>

        <div className="flex gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textTertiary" />
            <input
              type="text"
              placeholder={t('deviceAvlCapabilities.filterImei')}
              value={imeiFilter}
              onChange={(e) => setImeiFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              className="w-full pl-10 pr-4 py-2 bg-bgElevated border border-borderDefault rounded-lg text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative flex-1 min-w-[140px] max-w-[180px]">
            <input
              type="text"
              placeholder={t('deviceAvlCapabilities.filterRegNumber')}
              value={regNumberFilter}
              onChange={(e) => setRegNumberFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              className="w-full px-4 py-2 bg-bgElevated border border-borderDefault rounded-lg text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={t('deviceAvlCapabilities.filterAvlId')}
              value={avlIdFilter}
              onChange={(e) => setAvlIdFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              className="w-24 px-4 py-2 bg-bgElevated border border-borderDefault rounded-lg text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('common.filter')}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <LoadingState size="md" variant="default" />
        ) : data.length === 0 ? (
          <EmptyState
            icon={<DevicePhoneMobileIcon className="w-16 h-16" />}
            title={t('deviceAvlCapabilities.noData')}
          />
        ) : (
          <DataTable<DeviceAvlCapability & { _rowKey: string }>
            data={data.map((row, idx) => ({ ...row, _rowKey: `${row.imei}-${row.vin}-${idx}` }))}
            rowKey="_rowKey"
            columns={[
              {
                key: 'imei',
                label: t('deviceAvlCapabilities.imei'),
                sortable: true,
                csvValue: (v) => String(v ?? ''),
                render: (v, row) => (
                  <button
                    type="button"
                    onClick={() => setModalRow(row)}
                    className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    {String(v ?? '')}
                  </button>
                ),
              },
              { key: 'registration_number', label: t('deviceAvlCapabilities.regNumber'), sortable: true, render: (v) => <span className="font-mono text-sm">{String(v ?? '–')}</span> },
              { key: 'vin', label: t('deviceAvlCapabilities.vin'), sortable: true, render: (v) => <span className="font-mono text-sm">{String(v ?? '–')}</span> },
              {
                key: 'avl_parameters',
                label: t('deviceAvlCapabilities.avlParameters'),
                sortable: true,
                sortValue: (row) => (row.avl_parameters || []).length,
                csvValue: (_, row) => (row.avl_parameters || []).map((p: AvlParameter) => `${p.id}: ${p.name}`).join('; '),
                render: (_, row) => {
                  const count = (row.avl_parameters || []).length;
                  return (
                    <span className="font-mono text-sm text-textSecondary">
                      {count} {count === 1 ? t('deviceAvlCapabilities.parameterCountOne') : t('deviceAvlCapabilities.parameterCount')}
                    </span>
                  );
                },
              },
              { key: 'first_seen', label: t('deviceAvlCapabilities.firstSeen'), sortable: true, render: (v) => formatDate(v as string) },
              { key: 'last_seen', label: t('deviceAvlCapabilities.lastSeen'), sortable: true, render: (v) => formatDate(v as string) },
            ]}
            features={{ csvExport: true, search: true }}
          />
        )}
        {modalRow && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalRow(null)}>
            <div className="bg-bgElevated border border-borderDefault rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-borderDefault">
                <h2 className="text-lg font-semibold text-textDefault">{t('deviceAvlCapabilities.modalTitle')}</h2>
                <button type="button" onClick={() => setModalRow(null)} className="p-1 rounded hover:bg-bgHover text-textSecondary">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <dl className="grid grid-cols-1 gap-2 mb-4">
                  <div><dt className="text-xs text-textTertiary">{t('deviceAvlCapabilities.imei')}</dt><dd className="font-mono text-sm">{modalRow.imei}</dd></div>
                  <div><dt className="text-xs text-textTertiary">{t('deviceAvlCapabilities.regNumber')}</dt><dd className="font-mono text-sm">{modalRow.registration_number || '–'}</dd></div>
                  <div><dt className="text-xs text-textTertiary">{t('deviceAvlCapabilities.vin')}</dt><dd className="font-mono text-sm">{modalRow.vin || '–'}</dd></div>
                </dl>
                <h3 className="text-sm font-medium text-textDefault mb-2">{t('deviceAvlCapabilities.avlParameters')}</h3>
                <ul className="space-y-2">
                  {(modalRow.avl_parameters || []).map((p: AvlParameter) => (
                    <li key={p.id} className="p-3 bg-bgDefault rounded-lg border border-borderDefault">
                      <div className="font-medium text-textDefault"><span className="text-textTertiary font-mono mr-2">{p.id}</span>{p.name}</div>
                      {p.description && <div className="text-sm text-textSecondary mt-1">{p.description}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceAvlCapabilities;
